import { Link as RouterLink } from "react-router-dom";
import { getLicenseDurationLabel } from "src/util/enums";
import { f, yearFirstDateTimeFormat } from "src/utils/formatTime";
import { yearFirstDateTimeFormatInTimezone } from "./teamTimezoneFormat";
import { Link, Typography } from "@mui/material";
import { TeamActionMenu } from "./TeamActionMenu";
import { TeamMembersActionMenu } from "./TeamMembersActionMenu";
import Tooltip from "@mui/material/Tooltip";
import { communicationDefaultValue } from "../company/company.constants";
import { ActionsMenu } from "../Actions/ActionsMenu";
import viewFill from "@iconify/icons-eva/eye-fill";
import powerFill from "@iconify/icons-eva/power-fill";
import { LoadingButton } from "@mui/lab";

export const teamsColumns = (isReadOnly, timeZone) => [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
    resizable: true,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: 200,
        }}>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "active",
    headerName: "Status",
    flex: 1,
    minWidth: 100,
    resizable: true,
    renderCell: ({ value }) => (
      <span
        style={{ color: value.toLowerCase() === "y" ? "#1fd51f" : "#ff1616" }}
      >
        {value.toLowerCase() === "y" ? "Active" : "Inactive"}
      </span>
    ),
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1.2,
    minWidth: 200,
    resizable: true,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: 250,
        }}>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "creationTime",
    headerName: "Created On",
    type: "date",
    flex: 1,
    minWidth: 150,
    resizable: true,
    valueFormatter: ({ value }) => {
      return timeZone
        ? yearFirstDateTimeFormatInTimezone(value, timeZone)
        : yearFirstDateTimeFormat(value);
    },
    renderCell: ({ value }) => {
      const formatted = timeZone
        ? yearFirstDateTimeFormatInTimezone(value, timeZone)
        : yearFirstDateTimeFormat(value);
      return (
        <Tooltip title={formatted} placement="top">
          <span style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            maxWidth: 180,
          }}>{formatted}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "Actions",
    width: 70,
    minWidth: 70,
    sortable: false,
    resizable: false,
    renderCell: TeamActionMenu,
  },
];

export const teamBulkColumns = () => [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
    resizable: true,
  },
  {
    field: "address",
    headerName: "Address",
    flex: 2,
    minWidth: 200,
    resizable: true,
    renderCell: ({ value }) => {
      if (!value) return "";
      const { street } = value;
      const fullAddress = [street].filter(Boolean).join(", ");
      return (
        <div style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          maxWidth: 400, 
        }}>
          {fullAddress}
        </div>
      );
    },
  },
];

export const teamMembersColumns = (actionRequired, enableRedirection) => [
  {
    field: "username",
    headerName: "User Name",
    flex: 1,
    minWidth: 150,
    resizable: true,
    renderCell: ({ value, id }) => (
      <>
        {enableRedirection ? (
          <Link underline="none" component={RouterLink} to={`/team/${id}`}>
            {value}
          </Link>
        ) : (
          value
        )}
      </>
    ),
  },
  {
    field: "firstname",
    headerName: "Name",
    flex: 1,
    minWidth: 120,
    resizable: true,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    minWidth: 200,
    resizable: true,
  },
  {
    field: "accesstype",
    headerName: "Access Type",
    flex: 1,
    minWidth: 130,
    resizable: true,
  },
  {
    field: "Actions",
    width: 70,
    minWidth: 70,
    sortable: false,
    resizable: false,
    renderCell: TeamMembersActionMenu,
    hide: !actionRequired,
  },
];

export const teamsFormTabs = [
  { value: "general", label: "GENERAL" },
  { value: "invoice setting", label: "INVOICE SETTING" },
  { value: "payout accounts", label: "PAYOUT ACCOUNTS" },
  { value: "billing address", label: "BILLING ADDRESS" },
  { value: "members", label: "TEAM MEMBERS" },
  { value: "finance", label: "FINANCE CONFIGURATION" },
  { value: "payout schedule", label: "PAYOUT SCHEDULE" },
  { value: "subscription", label: "CHARGER SUBSCRIPTION" },
];

export const initialPayoutAccountObj = {
  business_name: "",
  business_type: "",
  beneficiary_name: "",
  email: "",
  phone: "",
  account_no: "",
  account_type: "",
  ifsc_code: "",
  gst: "",
  payout_percentage: 0,
  tax_percentage: 0,
  is_default: 0,
  creation: true,
  deleteBtn: true,
};

export const subscriptionDetailsColumns = (onViewDetails, isNotParentCompany = true, onDisableSubscription, hasFinanceConfigAccess = true) => [
  {
    field: "chargerId",
    headerName: "Charger ID",
    flex: 0.7,
    minWidth: 90,
    resizable: true,
  },
  {
    field: "chargerType",
    headerName: "Charger Type",
    flex: 0.5,
    minWidth: 80,
    resizable: true,
    renderCell: ({ value }) => (
      <span>{value || '-'}</span>
    ),
  },
  {
    field: "chargerCreationDate",
    headerName: "License Creation Date",
    flex: 0.9,
    minWidth: 100,
    resizable: true,
    renderCell: ({ value }) => (
      <span>{value ? f(value, "dd/MM/yyyy") : '-'}</span>
    ),
  },
  {
    field: "chargerSubscriptionExpiryDate",
    headerName: "Charger Subscription Expiry Date",
    flex: 0.9,
    minWidth: 100,
    resizable: true,
    renderCell: ({ value }) => (
      <span>{value ? f(value, "dd/MM/yyyy") : '-'}</span>
    ),
  },
  {
    field: "duration",
    headerName: "Duration",
    flex: 0.5,
    minWidth: 80,
    resizable: true,
    renderCell: ({ value }) => (
      <span>{getLicenseDurationLabel(value)}</span>
    ),
  },
  {
    field: "licenseId",
    headerName: "License ID",
    flex: 0.5,
    minWidth: 80,
    resizable: true,
     renderCell: ({ value }) => (
      <span>{value || '-'}</span>
    ),
  },
  {
    field: "final_expiry",
    headerName: "Last Renewal Date",
    flex: 0.9,
    minWidth: 100,
    resizable: true,
    renderCell: ({ value }) => (
      <span>{value ? f(value, "dd/MM/yyyy") : '-'}</span>
    ),
  },
  {
    field: "contractSummary",
    headerName: "Contract Summary",
    flex: 1,
    minWidth: 120,
    resizable: true,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: 150,
        }}>{value || '-'}</span>
      </Tooltip>
    ),
  },
  {
    field: "remarks",
    headerName: "Remarks",
    flex: 0.8,
    minWidth: 90,
    resizable: true,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: 120,
        }}>{value || '-'}</span>
      </Tooltip>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.6,
    minWidth: 80,
    resizable: true,
    renderCell: ({ value }) => {
      const statusConfig = {
        ACTIVE:   { label: "Active",   color: "#1fd51f" },
        INACTIVE: { label: "Inactive", color: "#ff9800" },
        EXPIRED:  { label: "Expired",  color: "#ff1616" },
        PENDING:  { label: "Pending",  color: "#9e9e9e" },
      };
      const cfg = statusConfig[value] ?? { label: value ?? "-", color: "#9e9e9e" };
      return (
        <span style={{ color: cfg.color, fontWeight: "500" }}>
          {cfg.label}
        </span>
      );
    },
  },
  ...(isNotParentCompany && hasFinanceConfigAccess ? [
    {
      field: "actions",
      headerName: "Actions",
      width: 70,
      minWidth: 70,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => {
        const subscriptionActionList = [
          {   
            id: "viewSubscription",
            title: "View Subscription",
            icon: viewFill,
            action: () => {
              if (onViewDetails) {
                onViewDetails(row);
              }
            },
          },
          {   
            id: "disable",
            title: row.status === "ACTIVE" ? "Disable" : "Enable",
            icon: powerFill,
            iconcolor: row.status === "ACTIVE" ? "#ff1616" : "#1fd51f",
            action: () => {
              if (onDisableSubscription && row.licensePk) {
                onDisableSubscription(row.licensePk);
              } else {
                console.error('onDisableSubscription not available or licensePk missing:', {
                  hasCallback: !!onDisableSubscription,
                  licensePk: row.licensePk,
                  fullRow: row
                });
              }
            },
          },
        ];
        
        return <ActionsMenu menuList={subscriptionActionList} />;
      },
    },
  ] : []),
];

export const withoutLicensetableColumns = (handleAddLicense, addingLicenseFor) => [
    {
      field: "chargebox_id",
      headerName: "Charge ID",
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        return params.value || "N/A";
      },
    },
    {
      field: "license_id",
      headerName: "License ID",
      flex: 1,
      renderCell: (params) => {
        return params.value || "N/A";
      },
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 1,
      renderCell: (params) => {
        return params.value || "N/A";
      },
    },
    {
      field: "summary",
      headerName: "Contract Summary",
      flex: 1,
      renderCell: (params) => {
        return params.value || "N/A";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        const chargerId = params.row.charge_box_pk || params.row.id;
        const isAdding = addingLicenseFor[chargerId];
        
        return (
          <LoadingButton
            variant="contained"
            color="primary"
            size="small"
            loading={isAdding}
            loadingPosition="start"
            onClick={() => handleAddLicense(params.row)}
            disabled={isAdding}
          >
            {isAdding ? "Adding..." : "Add License"}
          </LoadingButton>
        );
      },
    },
  ];

export const bulkNotificationColumns = () => [
  {
    field: "chargerId",
    headerName: "Charger ID",
    width: 150,
    sortable: true,
  },
  {
    field: "chargerSubscriptionExpiryDate",
    headerName: "Expiry Date",
    width: 120,
    sortable: true,
    renderCell: (params) => {
      const expiryDate = new Date(params.value);
      return expiryDate.toLocaleDateString('en-GB');
    },
  },
  {
    field: "daysUntilExpiry",
    headerName: "Days Until Expiry",
    width: 150,
    sortable: true,
    renderCell: (params) => {
      const expiryDate = new Date(params.row.chargerSubscriptionExpiryDate);
      const today = new Date();
      const daysDifference = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
      
      return (
        <Typography
          color={daysDifference <= 3 ? "error" : "warning.main"}
          fontWeight="medium"
        >
          {daysDifference} day{daysDifference !== 1 ? 's' : ''}
        </Typography>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 100,
    sortable: true,
    renderCell: (params) => (
      <Typography
        color={params.value === "ACTIVE" ? "success.main" : "error.main"}
        fontWeight="medium"
      >
        {params.value === "ACTIVE" ? "Active" : "Inactive"}
      </Typography>
    ),
  },
];

export const initialFinanceConfigObj = {
  charger_payment_details: {
    pricing_model: 'M',
    dc_charges_monthly: 400,
    ac_charges_monthly: 300,
    dc_charges_annual: 400,
    ac_charges_annual: 300,
    dc_charges_one_time: 400,
    ac_charges_one_time: 300,
    tax: 18,
    other_fixed_cost: "",
    trial_period: 7,
  },
  transaction_payment_details: {
    pg_charges_bearer: "CPO",
    payment_gateway_percent: 2.00,
    tax: 18,
  },
  payout_details: {
    payout_rule: 5,
    tax_bearer: "ZYNKA",
    tax: 18,
    chargemiles_commission_in_percent: true,
    chargemiles_commission_percent: 5,
    chargemiles_commission_cost: "",
    ignore_rfid_charges_in_payout: 0,
  },
};

export const possibleBusinessTypes = [
  { label: "Individual", value: "individual" },
  { label: "Proprietorship", value: "proprietorship" },
  { label: "Partnership", value: "partnership" },
  { label: "Private Limited", value: "private_limited" },
  { label: "Public Limited", value: "public_limited" },
  { label: "Limited Liability Partnership", value: "llp" },
  { label: "NonGovernmental Organization", value: "ngo" },
  { label: "Trust", value: "trust" },
  { label: "Society", value: "society" },
  { label: "Not Yet Registered", value: "not_yet_registered" },
  { label: "Hindu Undivided Family", value: "huf" },
];

export const possiblePayoutRules = [
  {
    label: "Fixed Amount per month or % per Unit (whichever is higher)",
    value: 1,
    disabled: true,
  },
  {
    label: "Fixed Amount per month or fixed per Unit (whichever is higher)",
    value: 2,
    disabled: true,
  },
  { label: "Fixed Amount per month and % per Unit", value: 3, disabled: true },
  {
    label: "Fixed Amount per month and fixed per Unit",
    value: 4,
    disabled: true,
  },
  { label: "% per Unit", value: 5 },
  { label: "fixed per Unit", value: 6 },
  { label: "Fixed Amount per month", value: 7, disabled: true },
];

// Maps each validated field (top-level key, incl. nested object roots) to the
// tab it lives on, so a failed Save can jump the user to the first errored tab.
export const teamFieldToTab = {
  name: "general",
  operatorName: "general",
  legal_name: "general",
  active: "general",
  street: "address",
  zipCode: "address",
  city: "address",
  country: "address",
  billingInfo: "invoice setting",
  payoutAccounts: "payout accounts",
  financeConfig: "finance",
  settlement_percentage: "finance",
};

// Order in which tabs are checked / surfaced to the user on error.
export const teamTabOrder = [
  "general",
  "invoice setting",
  "payout accounts",
  "address",
  "finance",
];

// Human-readable tab labels for the error message.
export const teamTabLabels = {
  general: "General",
  "invoice setting": "Invoice Setting",
  "payout accounts": "Payout Accounts",
  address: "Address",
  finance: "Finance",
};

export const getTeamDefaultValues = () => ({
  name: "",
  legal_name: "",
  active: "Y",
  description: "",
  operatorName: "",
  payoutAccounts: [initialPayoutAccountObj],
  financeConfig: initialFinanceConfigObj,
  locationLatitude: "",
  locationLongitude: "",
  street: "",
  houseNumber: "",
  addressLine3: "",
  zipCode: "",
  city: "",
  state: "",
  country: "",
  address: {
    addressId: "",
    street: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    country: "",
    lat: "",
    lng: "",
  },
  attachment: { url: "" },
  enable_license_payment: false,
  erp_company_id: null,
  advance_settings: {
    erp_company_id: null,
    erp_company_name: null
  },
  billingInfo: {
    hasGst: true,
    gstin: "",
    pan: "",
  },
  ...communicationDefaultValue()
});
